/**
 * 二次确认对话框(C-5 Web 适配)。用设计稿 .scrim/.dialog 类,不引入新 UI 库。
 * 契约:Esc = 取消语义;点遮罩 = 取消;确认/取消按钮语义与 App Alert 一致;
 * 打开时聚焦确认按钮 + 简易聚焦陷阱;确认中 disabled 防并发。
 */
import { useEffect, useRef } from 'react';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 确认按钮样式:destructive(不可逆操作)/ primary。 */
  confirmVariant?: 'destructive' | 'primary';
  submitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '确认',
  cancelLabel = '取消',
  confirmVariant = 'primary',
  submitting = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // 打开时聚焦确认按钮(仅在 open 切换时,不随 submitting 抖动)。
  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onCancel();
      if (e.key === 'Tab') {
        // 简易聚焦陷阱:限制在对话框内可聚焦元素间循环。
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        );
        // 提交中两按钮均 disabled → 无可聚焦项:仍拦住 Tab,防焦点逃逸到背景页。
        if (!focusables || focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title} ref={dialogRef}>
        <h3>{title}</h3>
        {description && <p className="sm muted">{description}</p>}
        <div className="row gap-4 mt-6" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`btn ${confirmVariant === 'destructive' ? 'btn-destructive' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? '处理中…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
